import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import useGetProfile from './hooks/useGetProfile';
import 'react-tooltip/dist/react-tooltip.css';

// Eagerly loaded pages (core, always needed)
import Index from './pages/Index';
import NewHomePage from './pages/NewHome';
import NotFound from './pages/NotFound';
import Agents from './pages/Agents';
import AgentDashboard from './pages/AgentDashboard';
import AboutUs from './pages/AboutUs';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import FAQ from './pages/FAQ';
import MyDashboard from './pages/MyDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AllStates from './pages/AllStates';
import PropertyTaxReport from './pages/PropertyTaxReport';
import HomePartner from './pages/HomePartner';
import SubjectPropertyPhotos from './pages/SubjectPropertyPhotos';
import NonDisclosureStates from './pages/NonDisclosureStates';
import HelpTest from './pages/HelpTest';
import Pricing from './pages/Pricing';
import HowToAppealPropertyTax from './pages/HowToAppealPropertyTax';
import LowerPropertyTaxBill from './pages/LowerPropertyTaxBill';
import PropertyTaxProtest from './pages/PropertyTaxProtest';
import AppealPropertyTaxValuation from './pages/AppealPropertyTaxValuation';
import PropertyTaxComparableSalesGuide from './pages/PropertyTaxComparableSalesGuide';
import PropertyTaxAppealDeadlines from './pages/PropertyTaxAppealDeadlines';
import PropertyTaxAppealFaq from './pages/PropertyTaxAppealFaq';
import Contact from './pages/Contact';
import Methodology from './pages/Methodology';
import LoanOfficerDashboard from './pages/LoanOfficerDashboard';

// Lazily loaded state pages — split into separate chunks
const Alabama = lazy(() => import('./pages/states/Alabama'));
const Alaska = lazy(() => import('./pages/states/Alaska'));
const Arizona = lazy(() => import('./pages/states/Arizona'));
const Arkansas = lazy(() => import('./pages/states/Arkansas'));
const California = lazy(() => import('./pages/states/California'));
const Colorado = lazy(() => import('./pages/states/Colorado'));
const Connecticut = lazy(() => import('./pages/states/Connecticut'));
const Delaware = lazy(() => import('./pages/states/Delaware'));
const DistrictOfColumbia = lazy(() => import('./pages/states/DistrictOfColumbia'));
const Florida = lazy(() => import('./pages/states/Florida'));
const Georgia = lazy(() => import('./pages/states/Georgia'));
const Hawaii = lazy(() => import('./pages/states/Hawaii'));
const Idaho = lazy(() => import('./pages/states/Idaho'));
const Illinois = lazy(() => import('./pages/states/Illinois'));
const Indiana = lazy(() => import('./pages/states/Indiana'));
const Iowa = lazy(() => import('./pages/states/Iowa'));
const Kansas = lazy(() => import('./pages/states/Kansas'));
const Kentucky = lazy(() => import('./pages/states/Kentucky'));
const Louisiana = lazy(() => import('./pages/states/Louisiana'));
const Maine = lazy(() => import('./pages/states/Maine'));
const Maryland = lazy(() => import('./pages/states/Maryland'));
const Massachusetts = lazy(() => import('./pages/states/Massachusetts'));
const Michigan = lazy(() => import('./pages/states/Michigan'));
const Minnesota = lazy(() => import('./pages/states/Minnesota'));
const Mississippi = lazy(() => import('./pages/states/Mississippi'));
const Missouri = lazy(() => import('./pages/states/Missouri'));
const Montana = lazy(() => import('./pages/states/Montana'));
const Nebraska = lazy(() => import('./pages/states/Nebraska'));
const Nevada = lazy(() => import('./pages/states/Nevada'));
const NewHampshire = lazy(() => import('./pages/states/NewHampshire'));
const NewJersey = lazy(() => import('./pages/states/NewJersey'));
const NewMexico = lazy(() => import('./pages/states/NewMexico'));
const NewYork = lazy(() => import('./pages/states/NewYork'));
const NorthCarolina = lazy(() => import('./pages/states/NorthCarolina'));
const NorthDakota = lazy(() => import('./pages/states/NorthDakota'));
const Ohio = lazy(() => import('./pages/states/Ohio'));
const Oklahoma = lazy(() => import('./pages/states/Oklahoma'));
const Oregon = lazy(() => import('./pages/states/Oregon'));
const Pennsylvania = lazy(() => import('./pages/states/Pennsylvania'));
const RhodeIsland = lazy(() => import('./pages/states/RhodeIsland'));
const SouthCarolina = lazy(() => import('./pages/states/SouthCarolina'));
const SouthDakota = lazy(() => import('./pages/states/SouthDakota'));
const Tennessee = lazy(() => import('./pages/states/Tennessee'));
const Texas = lazy(() => import('./pages/states/Texas'));
const Utah = lazy(() => import('./pages/states/Utah'));
const Vermont = lazy(() => import('./pages/states/Vermont'));
const Virginia = lazy(() => import('./pages/states/Virginia'));
const Washington = lazy(() => import('./pages/states/Washington'));
const WestVirginia = lazy(() => import('./pages/states/WestVirginia'));
const Wisconsin = lazy(() => import('./pages/states/Wisconsin'));
const Wyoming = lazy(() => import('./pages/states/Wyoming'));

const App = () => {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

  const { data: profile } = useGetProfile();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      getAccessTokenSilently()
        .then((res) => {
          const existingToken = window.localStorage.getItem('_token');
          window.localStorage.setItem('_token', res);

          // Only reload if we didn't have a token before and now we do
          if (!existingToken && res) {
            window.location.reload();
          }
        })
        .catch(console.log);
    } else if (!isLoading && !isAuthenticated) {
      window.localStorage.removeItem('_token');
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  const renderDashboard = useMemo(() => {
    switch (profile?.role) {
      case 'ADMIN':
        return <AdminDashboard />;
      case 'AGENT':
        return <AgentDashboard />;
      case 'LOAN_OFFICER':
        return <LoanOfficerDashboard />;
      default:
        return <MyDashboard />;
    }
  }, [profile?.role]);

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<NewHomePage />} />
            <Route path="/home-partner" element={<HomePartner />} />
            <Route path="/agents" element={<Agents />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/methodology" element={<Methodology />} />
            <Route path="/dashboard" element={renderDashboard} />
            <Route path="/guides" element={<AllStates />} />
            <Route path="/all-states" element={<AllStates />} />
            <Route path="/deadlines" element={<AllStates />} />
            <Route path="/property-tax-report/:id" element={<PropertyTaxReport />} />
            <Route path="/property-photos" element={<SubjectPropertyPhotos />} />
            <Route path="/non-disclosure-states" element={<NonDisclosureStates />} />
            <Route path="/help" element={<HelpTest />} />
            <Route path="/how-to-appeal-property-tax" element={<HowToAppealPropertyTax />} />
            <Route path="/lower-property-tax-bill" element={<LowerPropertyTaxBill />} />
            <Route path="/property-tax-protest" element={<PropertyTaxProtest />} />
            <Route path="/appeal-property-tax-valuation" element={<AppealPropertyTaxValuation />} />
            <Route path="/property-tax-comparable-sales-guide" element={<PropertyTaxComparableSalesGuide />} />
            <Route path="/property-tax-appeal-deadlines" element={<PropertyTaxAppealDeadlines />} />
            <Route path="/property-tax-appeal-faq" element={<PropertyTaxAppealFaq />} />

            {/* State pages — lazily loaded */}
            <Route path="/alabama" element={<Alabama />} />
            <Route path="/alaska" element={<Alaska />} />
            <Route path="/arizona" element={<Arizona />} />
            <Route path="/arkansas" element={<Arkansas />} />
            <Route path="/california" element={<California />} />
            <Route path="/colorado" element={<Colorado />} />
            <Route path="/connecticut" element={<Connecticut />} />
            <Route path="/delaware" element={<Delaware />} />
            <Route path="/district-of-columbia" element={<DistrictOfColumbia />} />
            <Route path="/florida" element={<Florida />} />
            <Route path="/georgia" element={<Georgia />} />
            <Route path="/hawaii" element={<Hawaii />} />
            <Route path="/idaho" element={<Idaho />} />
            <Route path="/illinois" element={<Illinois />} />
            <Route path="/indiana" element={<Indiana />} />
            <Route path="/iowa" element={<Iowa />} />
            <Route path="/kansas" element={<Kansas />} />
            <Route path="/kentucky" element={<Kentucky />} />
            <Route path="/louisiana" element={<Louisiana />} />
            <Route path="/maine" element={<Maine />} />
            <Route path="/maryland" element={<Maryland />} />
            <Route path="/massachusetts" element={<Massachusetts />} />
            <Route path="/michigan" element={<Michigan />} />
            <Route path="/minnesota" element={<Minnesota />} />
            <Route path="/mississippi" element={<Mississippi />} />
            <Route path="/missouri" element={<Missouri />} />
            <Route path="/montana" element={<Montana />} />
            <Route path="/nebraska" element={<Nebraska />} />
            <Route path="/nevada" element={<Nevada />} />
            <Route path="/new-hampshire" element={<NewHampshire />} />
            <Route path="/new-jersey" element={<NewJersey />} />
            <Route path="/new-mexico" element={<NewMexico />} />
            <Route path="/new-york" element={<NewYork />} />
            <Route path="/north-carolina" element={<NorthCarolina />} />
            <Route path="/north-dakota" element={<NorthDakota />} />
            <Route path="/ohio" element={<Ohio />} />
            <Route path="/oklahoma" element={<Oklahoma />} />
            <Route path="/oregon" element={<Oregon />} />
            <Route path="/pennsylvania" element={<Pennsylvania />} />
            <Route path="/rhode-island" element={<RhodeIsland />} />
            <Route path="/south-carolina" element={<SouthCarolina />} />
            <Route path="/south-dakota" element={<SouthDakota />} />
            <Route path="/tennessee" element={<Tennessee />} />
            <Route path="/texas" element={<Texas />} />
            <Route path="/utah" element={<Utah />} />
            <Route path="/vermont" element={<Vermont />} />
            <Route path="/virginia" element={<Virginia />} />
            <Route path="/washington" element={<Washington />} />
            <Route path="/west-virginia" element={<WestVirginia />} />
            <Route path="/wisconsin" element={<Wisconsin />} />
            <Route path="/wyoming" element={<Wyoming />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  );
};

export default App;
